// Platform-admin DO read-only inspection — audits to Loki as admin_debug_read; 403 non-admin; 60/hour.
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { confirm } from '../lib/utils.js';
import { runDebugObservability } from './observability.js';
import { parseReportEditArgs } from '../lib/report-edit-args.js';
import {
  findGitRoot,
  resolveHubFolderBySelector,
  scanWorkspaceHubs,
} from '../lib/workspace.js';
import { readBinding } from '../lib/hub-binding.js';
import { HARNESS_SKILL_DIRS } from '../lib/skill-version.js';
import { resolveTemplatesDir, resolveHubsDir, hubsDirLabel } from '../lib/layout.js';
import { readDirToFileMap, writeFileMap, writeSliceSnapshot, listDirRelFiles } from '../lib/template-files.js';
import { sanitizeWayaiSliceFiles } from '../lib/template-sanitize.js';
import { buildTemplatePushCommon } from '../lib/template-manifest.js';
import {
  dataExplorerDebugDoType,
  dataExplorerDebugAnalyticsTable,
  templateYamlSchema,
  templateLocaleSchema,
  TEMPLATE_SLUG_REGEX,
  ADMIN_SKILL_NAME_REGEX,
  sandboxEgressPolicy,
} from '@wayai/core/contracts';
import type {
  DataExplorerDebugDoType,
  DataExplorerDebugAnalyticsTable,
  CreateNoticeBody,
  NoticeSeverity,
  TemplatePushBody,
  TemplatePushLocale,
  TemplateLocale,
  TemplateYaml,
  SandboxEgressPolicy,
  AdminSandboxExecBody,
  AdminHarnessMassDestroyInput,
} from '@wayai/core/contracts';

type DoType = DataExplorerDebugDoType;
type AnalyticsTable = DataExplorerDebugAnalyticsTable;
// Single source of truth: the contract enums the backend allowlist also derives
// from. The CLI bundles @wayai/core at build (tsup `noExternal`), so importing
// the runtime enum is free — no hand-copied literal, no drift to guard.
const VALID_TYPES = dataExplorerDebugDoType.options;
const VALID_ANALYTICS_TABLES = dataExplorerDebugAnalyticsTable.options;

export async function adminCommand(args: string[]): Promise<void> {
  const [group, ...afterGroup] = args;

  if (!group) {
    printHelp();
    process.exit(1);
  }

  if (group === 'archive') {
    // `archive` has no sub — all remaining args are positional/flags.
    const { positional, flagArgs } = splitArgs(afterGroup);
    await runArchiveRead(positional, flagArgs);
    return;
  }

  if (group === 'observability') {
    const { positional, flagArgs } = splitArgs(afterGroup);
    await runObservabilityRead(positional, flagArgs);
    return;
  }

  const [sub, ...rest] = afterGroup;
  const { positional, flagArgs } = splitArgs(rest);

  if (group === 'do') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'tables': await runDoTables(positional); return;
      case 'read': await runDoRead(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'analytics') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'tables': await runAnalyticsTables(); return;
      case 'read': await runAnalyticsRead(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'report') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'list': await runReportList(flagArgs); return;
      case 'show': await runReportShow(positional); return;
      case 'group': await runReportGroup(positional); return;
      case 'ungroup': await runReportUngroup(positional); return;
      case 'escalate': await runReportEscalate(positional, flagArgs); return;
      case 'shipped': await runReportShipped(positional, flagArgs); return;
      case 'addressed': await runReportAddressed(positional); return;
      case 'dismiss': await runReportDismiss(positional, flagArgs); return;
      case 'hold': await runReportHold(positional, flagArgs); return;
      case 'update': await runReportUpdate(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'notice') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'create': await runNoticeCreate(flagArgs); return;
      case 'list': await runNoticeList(flagArgs); return;
      case 'resolve': await runNoticeResolve(positional); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'template') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'push': await runTemplatePush(positional); return;
      case 'pull': await runTemplatePull(positional, flagArgs); return;
      case 'freeze': await runTemplateFreeze(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'skill') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'install': await runSkillInstall(positional); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'sandbox') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      // The command is positional after `exec`; the rest are flags.
      case 'exec': await runSandboxExec(positional, flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  if (group === 'harness') {
    if (!sub) { printHelp(); process.exit(1); }
    switch (sub) {
      case 'mass-destroy': await runHarnessMassDestroy(flagArgs); return;
      case 'disable': await runHarnessToggle(false, flagArgs); return;
      case 'enable': await runHarnessToggle(true, flagArgs); return;
      case 'status': await runHarnessStatus(flagArgs); return;
      default: printHelp(); process.exit(1);
    }
  }

  printHelp();
  process.exit(1);
}

function splitArgs(args: string[]): { positional: string[]; flagArgs: string[] } {
  const positional: string[] = [];
  const flagArgs: string[] = [];
  for (const a of args) {
    if (a.startsWith('--') || flagArgs.length > 0) {
      flagArgs.push(a);
    } else {
      positional.push(a);
    }
  }
  return { positional, flagArgs };
}

// ---------------------------------------------------------------------------
// Subcommands — DO
// ---------------------------------------------------------------------------

async function runDoTables(positional: string[]): Promise<void> {
  if (positional.length < 2) {
    console.error('wayai admin do tables <type> <id>');
    process.exit(1);
  }
  const type = validateType(positional[0]);
  const id = positional[1];

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugListTables(type, id);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const { tables } = response.data;
  if (tables.length === 0) {
    console.log('(no allowlisted tables found on this DO)');
    return;
  }
  for (const t of tables) console.log(t);
}

async function runDoRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 3) {
    console.error('wayai admin do read <type> <id> <table> [--limit N] [--id X] [--json]');
    process.exit(1);
  }
  const type = validateType(positional[0]);
  const id = positional[1];
  const table = positional[2];

  const { limit, filterId, jsonOutput } = parseFlags(flagArgs, ['--limit', '--id', '--json']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugReadTable(type, id, table, { limit, id: filterId });
  } catch (err) {
    exitOnApiError(err);
    throw err;
  }

  printDebugRowsResult(response.data, jsonOutput);
}

// ---------------------------------------------------------------------------
// Subcommands — Analytics (ClickHouse)
// ---------------------------------------------------------------------------

async function runAnalyticsTables(): Promise<void> {
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugListAnalyticsTables();
  } catch (err) {
    exitOnApiError(err);
    throw err;
  }

  const { tables } = response.data;
  if (tables.length === 0) {
    console.log('(no analytics tables available)');
    return;
  }
  for (const t of tables) console.log(t);
}

async function runAnalyticsRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 1) {
    console.error('wayai admin analytics read <table> [--conversation-id X] [--hub-id Y] [--message-id Z] [--limit N] [--json]');
    process.exit(1);
  }
  const table = validateAnalyticsTable(positional[0]);

  const flags = parseFlags(flagArgs, ['--limit', '--json', '--conversation-id', '--hub-id', '--message-id']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugReadAnalyticsTable(table, {
      conversationId: flags.conversationId,
      hubId: flags.hubId,
      messageId: flags.messageId,
      limit: flags.limit,
    });
  } catch (err) {
    exitOnApiError(err);
    throw err;
  }

  printDebugRowsResult(response.data, flags.jsonOutput);
}

// ---------------------------------------------------------------------------
// Subcommands — Archive (R2)
// ---------------------------------------------------------------------------

async function runArchiveRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 2) {
    console.error('wayai admin archive <hub_id> <conversation_id> [--message-id X] [--json]');
    process.exit(1);
  }
  const hubId = positional[0];
  const conversationId = positional[1];

  const flags = parseFlags(flagArgs, ['--json', '--message-id']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.debugReadArchive(hubId, conversationId, {
      messageId: flags.messageId,
    });
  } catch (err) {
    // Archive-specific 404 copy: an admin reading an archive expects "purged or never written"
    // rather than a generic "not found." Only this subcommand has the context to say that.
    if (err instanceof ApiError && err.status === 404) {
      console.error('Archive not found. The conversation may not be ended yet, or the R2 blob may have been purged.');
      process.exit(1);
    }
    exitOnApiError(err);
    throw err;
  }

  const data = response.data;

  if (flags.jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  // Default pretty-print: archive metadata + a one-line message digest.
  // Admins who need full content pass --json.
  const archive = data.archive;
  console.log(`hub_id:         ${data.hub_id}`);
  console.log(`conversation_id:${data.conversation_id}`);
  if (data.message_filtered) console.log('(messages filtered by --message-id)');

  const messages = Array.isArray(archive.messages) ? (archive.messages as Array<Record<string, unknown>>) : [];
  const files = Array.isArray(archive.files) ? archive.files.length : 0;
  console.log(`messages:       ${messages.length}`);
  console.log(`files:          ${files}`);

  if (messages.length > 0) {
    console.log('');
    console.log('messages (id | sender | created_at | text):');
    for (const m of messages) {
      const id = String(m.message_id ?? '');
      const sender = String(m.sender_type ?? '');
      const ts = String(m.created_at ?? '');
      const text = truncate(String(m.message_text ?? ''), 80);
      console.log(`  ${id} | ${sender} | ${ts} | ${text}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Observability (R2)
// ---------------------------------------------------------------------------

async function runObservabilityRead(positional: string[], flagArgs: string[]): Promise<void> {
  if (positional.length < 2) {
    console.error('wayai admin observability <hub_id> <conversation_id> [--message-id X] [--json]');
    process.exit(1);
  }
  const hubId = positional[0];
  const conversationId = positional[1];

  const flags = parseFlags(flagArgs, ['--json', '--message-id']);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  try {
    await runDebugObservability(client, hubId, conversationId, {
      messageId: flags.messageId,
      jsonOutput: flags.jsonOutput,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.error('Observability data not found. Either the conversation has no LLM turns yet, or R2 entries were purged.');
      process.exit(1);
    }
    exitOnApiError(err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function validateType(raw: string | undefined): DoType {
  if (!raw || !VALID_TYPES.includes(raw as DoType)) {
    console.error(`type must be one of: ${VALID_TYPES.join(', ')}`);
    process.exit(1);
  }
  return raw as DoType;
}

function validateAnalyticsTable(raw: string | undefined): AnalyticsTable {
  if (!raw || !VALID_ANALYTICS_TABLES.includes(raw as AnalyticsTable)) {
    console.error(`table must be one of: ${VALID_ANALYTICS_TABLES.join(', ')}`);
    process.exit(1);
  }
  return raw as AnalyticsTable;
}

interface ParsedFlags {
  limit?: number;
  filterId?: string;
  conversationId?: string;
  hubId?: string;
  messageId?: string;
  jsonOutput: boolean;
}

function parseFlags(flagArgs: string[], allowed: readonly string[]): ParsedFlags {
  const out: ParsedFlags = { jsonOutput: false };
  for (let i = 0; i < flagArgs.length; i++) {
    const arg = flagArgs[i];
    if (!allowed.includes(arg)) {
      console.error(`Unknown flag: ${arg}`);
      process.exit(1);
    }
    if (arg === '--json') {
      out.jsonOutput = true;
      continue;
    }
    const v = flagArgs[++i];
    if (!v) { console.error(`${arg} requires a value`); process.exit(1); }
    if (arg === '--limit') {
      const n = Number.parseInt(v, 10);
      if (!Number.isFinite(n)) { console.error('--limit must be an integer'); process.exit(1); }
      if (n < 1) { console.error('--limit must be >= 1'); process.exit(1); }
      out.limit = n;
    } else if (arg === '--id') {
      out.filterId = v;
    } else if (arg === '--conversation-id') {
      out.conversationId = v;
    } else if (arg === '--hub-id') {
      out.hubId = v;
    } else if (arg === '--message-id') {
      out.messageId = v;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Subcommands — Templates gallery authoring (wayai-admin skill)
// ---------------------------------------------------------------------------

/**
 * Resolve + validate a template's `template.yaml` from the authoring repo. Shared by
 * `push` and `freeze`: both need the git root, the template dir, and a parsed manifest
 * whose slug matches the folder. Exits the process with a clear message on any failure.
 */
function loadTemplateManifest(slug: string): { gitRoot: string; templateDir: string; manifest: TemplateYaml } {
  const gitRoot = findGitRoot();
  if (!gitRoot) {
    console.error('Not in a git repository. Run from the tutorials/ authoring repo.');
    process.exit(1);
  }

  const templateDir = path.join(resolveTemplatesDir(gitRoot), slug);
  const manifestPath = path.join(templateDir, 'template.yaml');
  if (!fs.existsSync(manifestPath)) {
    console.error(`template.yaml not found at ${path.relative(gitRoot, manifestPath)}`);
    process.exit(1);
  }

  // Parse + validate template.yaml against the shared @wayai/core schema.
  let manifest: TemplateYaml;
  try {
    manifest = templateYamlSchema.parse(yaml.load(fs.readFileSync(manifestPath, 'utf-8')));
  } catch (err) {
    console.error(`Invalid template.yaml: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }
  if (manifest.slug !== slug) {
    console.error(`template.yaml slug "${manifest.slug}" does not match folder "${slug}".`);
    process.exit(1);
  }

  return { gitRoot, templateDir, manifest };
}

async function runTemplatePush(positional: string[]): Promise<void> {
  const slug = positional[0];
  if (!slug) {
    console.error('wayai admin template push <slug>');
    process.exit(1);
  }
  if (!TEMPLATE_SLUG_REGEX.test(slug)) {
    console.error(`Invalid template slug: ${slug}`);
    process.exit(1);
  }

  const { templateDir, manifest } = loadTemplateManifest(slug);

  // The authored locales are the wayai locale map (the wayai slice is required; the
  // schema guarantees defaultLocale is present). gh #2955: the channel no longer
  // bundles a Rekor slice, so there is no second locale map to union.
  const authoredLocales = new Set<TemplateLocale>(
    Object.keys(manifest.wayai.locales) as TemplateLocale[],
  );

  // Build the per-locale push payload. For each locale: read its wayai slice (the
  // authoring-only `source` pointer), its README.<locale>.md (RAW markdown — the
  // backend renders + sanitizes, single authority), and its localized SEO text.
  const locales: Partial<Record<TemplateLocale, TemplatePushLocale>> = {};
  for (const locale of authoredLocales) {
    const wayaiEntry = manifest.wayai.locales[locale];

    let wayaiFiles: Record<string, string> | undefined;
    if (wayaiEntry) {
      const sliceDir = path.resolve(templateDir, wayaiEntry.source);
      if (!fs.existsSync(sliceDir)) {
        console.error(`wayai source not found for ${locale}: ${wayaiEntry.source} (resolved ${sliceDir})`);
        process.exit(1);
      }
      // Nudge toward a committed frozen slice (gh #2960): a `source:` still pointing at a
      // live workspace hub goes stale when that hub is recreated under a new instance id.
      // Non-fatal — a live source still publishes fine; this just isn't silent anymore.
      if (!isInsideTemplateDir(sliceDir, templateDir)) {
        console.warn(
          `Note: ${locale} source points at a live hub (${wayaiEntry.source}); it can go stale ` +
          `on a hub recreate. Run \`wayai admin template freeze ${slug} --locale ${locale} --from ${path.basename(sliceDir)}\` ` +
          `to commit a stable frozen slice.`,
        );
      }
      wayaiFiles = readDirToFileMap(sliceDir);
    }

    const readmePath = path.join(templateDir, `README.${locale}.md`);
    const readmeMarkdown = fs.existsSync(readmePath) ? fs.readFileSync(readmePath, 'utf-8') : '';

    locales[locale] = {
      readmeMarkdown,
      ...(wayaiFiles && { wayaiFiles }),
      ...(wayaiEntry && {
        wayai: { metaTitle: wayaiEntry.metaTitle, metaDescription: wayaiEntry.metaDescription },
      }),
    };
  }

  // Hero (optional, locale-agnostic) → base64 for the inline push.
  let hero: TemplatePushBody['common']['hero'];
  if (manifest.hero) {
    const heroPath = path.resolve(templateDir, manifest.hero);
    if (fs.existsSync(heroPath)) {
      hero = { filename: path.basename(heroPath), dataBase64: fs.readFileSync(heroPath).toString('base64') };
    } else {
      console.error(`Warning: hero not found at ${manifest.hero}; skipping.`);
    }
  }

  const body: TemplatePushBody = {
    slug,
    defaultLocale: manifest.defaultLocale,
    common: buildTemplatePushCommon(manifest, hero),
    locales,
  };

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let result;
  try {
    result = await client.adminTemplatePush(body);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  console.log(`Template "${result.slug}": ${result.action} (${result.filesWritten} written, ${result.filesDeleted} deleted).`);
  if (manifest.status !== 'published') {
    console.log(`(status=${manifest.status} → unpublished from the store)`);
  }
}

/**
 * Is `dest` the template dir itself or a path inside it? A frozen slice must live under
 * `wayai-templates/<slug>/`; a `source:` that escapes (still pointing at a live
 * `../../wayai-ws/...` hub) is the staleness `freeze` exists to fix (gh #2960).
 */
function isInsideTemplateDir(dest: string, templateDir: string): boolean {
  return dest === templateDir || dest.startsWith(templateDir + path.sep);
}

/**
 * `wayai admin template freeze <slug> [--locale <l>] [--from <hub>] [--dry-run]`
 *
 * Snapshot a live workspace hub into the template's committed slice dir (the path the
 * locale's `source:` resolves to), scrubbed exactly as push scrubs it (instance ids +
 * author PII). The committed slice is then a stable authoring source: re-push survives a
 * hub recreate, decoupling the iterated live hub from the published artifact (gh #2960).
 *
 * Purely local — reads the on-disk hub folder, writes the slice, no platform call. Run
 * `wayai pull` first if you want the latest config from the platform before freezing.
 */
async function runTemplateFreeze(positional: string[], flagArgs: string[]): Promise<void> {
  const slug = positional[0];
  if (!slug) {
    console.error('wayai admin template freeze <slug> [--locale <l>] [--from <hub>] [--dry-run]');
    process.exit(1);
  }
  if (!TEMPLATE_SLUG_REGEX.test(slug)) {
    console.error(`Invalid template slug: ${slug}`);
    process.exit(1);
  }

  let locale: TemplateLocale | undefined;
  let from: string | undefined;
  let dryRun = false;
  for (let i = 0; i < flagArgs.length; i++) {
    if (flagArgs[i] === '--dry-run') { dryRun = true; continue; }
    if (flagArgs[i] === '--locale') {
      const v = flagArgs[++i];
      if (!v) { console.error('--locale requires a value'); process.exit(1); }
      const parsed = templateLocaleSchema.safeParse(v);
      if (!parsed.success) {
        console.error(`Invalid --locale "${v}": must be one of ${templateLocaleSchema.options.join(', ')}.`);
        process.exit(1);
      }
      locale = parsed.data;
      continue;
    }
    if (flagArgs[i] === '--from') {
      from = flagArgs[++i];
      if (!from) { console.error('--from requires a value'); process.exit(1); }
      continue;
    }
    console.error(`Unknown flag: ${flagArgs[i]}`);
    process.exit(1);
  }

  const { gitRoot, templateDir, manifest } = loadTemplateManifest(slug);

  // Pick the locale to freeze. Each locale freezes from its own hub, so with >1 authored
  // locale we can't guess which hub maps to which — require --locale.
  const authored = Object.keys(manifest.wayai.locales) as TemplateLocale[];
  let targetLocale: TemplateLocale;
  if (locale) {
    if (!manifest.wayai.locales[locale]) {
      console.error(`Locale "${locale}" is not authored in ${slug}/template.yaml (have: ${authored.join(', ')}).`);
      process.exit(1);
    }
    targetLocale = locale;
  } else if (authored.length === 1) {
    targetLocale = authored[0];
  } else {
    console.error(`Template "${slug}" authors ${authored.length} locales (${authored.join(', ')}). Pass --locale <l> — each freezes from its own hub.`);
    process.exit(1);
  }

  // The frozen slice must be a committed SUBDIR of the template dir. `dest === templateDir`
  // is refused: writeSliceSnapshot makes dest equal the slice, which would delete
  // template.yaml / README / assets sitting at the template root.
  const entry = manifest.wayai.locales[targetLocale]!;
  const dest = path.resolve(templateDir, entry.source);
  if (!isInsideTemplateDir(dest, templateDir) || dest === templateDir) {
    console.error(
      `source for "${targetLocale}" must be a committed path inside ${slug}/ (e.g. "./${targetLocale}/slice"), but resolved to:\n` +
      `  ${entry.source} → ${dest}\n` +
      `Set it to a subdir of the template folder and re-run freeze.`,
    );
    process.exit(1);
  }

  // Resolve the live source hub: an explicit --from selector, else the worktree's
  // `wayai use` binding (a UUID — resolved through the same scan-by-hub_id path).
  const hubsDir = resolveHubsDir(gitRoot);
  const selector = from ?? readBinding();
  const hubFolder = selector ? resolveHubFolderBySelector(hubsDir, selector) : null;
  if (!hubFolder) {
    console.error(
      selector
        ? `Could not resolve a source hub matching "${selector}" in ${hubsDirLabel(gitRoot)}/.`
        : 'No --from given and this worktree has no `wayai use` binding.',
    );
    const hubs = scanWorkspaceHubs(hubsDir);
    if (hubs.length) {
      console.error('Available hubs:');
      for (const h of hubs) console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
    }
    console.error(`\nPass --from <folder|uuid>${selector ? '.' : ' or run `wayai use <hub>` first.'}`);
    process.exit(1);
  }

  // Read + scrub the live slice (instance ids + author PII) — the same scrub push applies
  // server-side, run here so the committed bytes are clean before they reach git.
  const liveFiles = readDirToFileMap(hubFolder);
  if (Object.keys(liveFiles).length === 0) {
    console.error(`Source hub folder has no files to freeze: ${path.relative(gitRoot, hubFolder)}`);
    process.exit(1);
  }
  const sanitized = sanitizeWayaiSliceFiles(liveFiles);

  const relDest = path.relative(gitRoot, dest);
  const relSrc = path.relative(gitRoot, hubFolder);

  if (dryRun) {
    const existing = new Set(listDirRelFiles(dest));
    const nextKeys = new Set(Object.keys(sanitized));
    console.log(`Dry run — would freeze ${relSrc}/ → ${relDest}/ (locale ${targetLocale}):`);
    for (const rel of Object.keys(sanitized).sort((a, b) => a.localeCompare(b))) {
      console.log(`  ${existing.has(rel) ? 'update' : 'create'}  ${rel}`);
    }
    for (const rel of [...existing].filter((k) => !nextKeys.has(k)).sort((a, b) => a.localeCompare(b))) {
      console.log(`  remove  ${rel}`);
    }
    return;
  }

  const { written, removed } = writeSliceSnapshot(dest, sanitized);
  console.log(
    `Froze ${relSrc}/ → ${relDest}/ (locale ${targetLocale}): ` +
    `${written.length} file${written.length === 1 ? '' : 's'} written` +
    `${removed.length ? `, ${removed.length} removed` : ''}.`,
  );
  console.log(`The slice is committed — re-push is stable across hub recreates. Next: \`wayai admin template push ${slug}\`.`);
}

async function runTemplatePull(positional: string[], flagArgs: string[]): Promise<void> {
  const slug = positional[0];
  if (!slug) {
    console.error('wayai admin template pull <slug> [--out DIR] [--json]');
    process.exit(1);
  }
  if (!TEMPLATE_SLUG_REGEX.test(slug)) {
    console.error(`Invalid template slug: ${slug}`);
    process.exit(1);
  }
  // Minimal inline flag parse (--out DIR, --json) — the shared parseFlags is fixed-purpose.
  let outDir: string | undefined;
  let jsonOutput = false;
  for (let i = 0; i < flagArgs.length; i++) {
    if (flagArgs[i] === '--json') { jsonOutput = true; continue; }
    if (flagArgs[i] === '--out') {
      outDir = flagArgs[++i];
      if (!outDir) { console.error('--out requires a value'); process.exit(1); }
      continue;
    }
    console.error(`Unknown flag: ${flagArgs[i]}`);
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let detail;
  try {
    detail = await client.adminTemplatePull(slug);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  if (jsonOutput) {
    console.log(JSON.stringify(detail, null, 2));
    return;
  }

  const target = outDir ? path.resolve(outDir) : path.join(process.cwd(), 'template-store-pull', slug);
  const written = writeFileMap(target, detail.files);
  console.log(`Pulled "${slug}" from the store → ${target} (${written.length} file${written.length === 1 ? '' : 's'}).`);
  // `uses` may be absent when the store predates it (eventually-consistent read).
  const uses = detail.uses ?? [];
  const usesLabel = uses.length ? uses.map((u) => `${u.backend}/${u.endpoint}`).join(', ') : '(none)';
  console.log(`includes: wayai=${detail.includes.wayai} | uses: ${usesLabel}`);
}

// ---------------------------------------------------------------------------
// Subcommands — operator skill install (private, platform-admin gated)
// ---------------------------------------------------------------------------

async function runSkillInstall(positional: string[]): Promise<void> {
  const name = positional[0] ?? 'wayai-admin';
  if (!ADMIN_SKILL_NAME_REGEX.test(name)) {
    console.error(`Invalid skill name: ${name}`);
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let res;
  try {
    res = await client.adminSkillInstall(name);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  // Anchor at the repo root so each harness discovers the skill under its own
  // skills/ dir; fall back to cwd outside a git repo. Use the locally-validated
  // `name` for the write dir (not the server-echoed res.name) — no trusted-server assumption.
  const root = findGitRoot() ?? process.cwd();

  // Unlike the PUBLIC skill — which the external `npx skills` installer fans
  // across every harness — the admin skill is private/backend-bundled, so THIS
  // command is the only install path and must carry the multi-harness fan-out
  // itself. Write into each recognized harness dir already present in the repo;
  // if none are present, write all of them so a non-Claude operator's harness
  // still discovers the skill.
  const present = HARNESS_SKILL_DIRS.filter((dir) => fs.existsSync(path.join(root, dir)));
  const targets = present.length > 0 ? present : HARNESS_SKILL_DIRS;

  // Same file set lands in every harness dir, so count once up front.
  const fileCount = Object.keys(res.files).length;
  const relDirs = targets.map((harness) => {
    const targetDir = path.join(root, harness, 'skills', name);
    writeFileMap(targetDir, res.files);
    return `${path.relative(root, targetDir)}/`;
  });

  console.log(`Installed skill "${name}" (${fileCount} file${fileCount === 1 ? '' : 's'}) → ${relDirs.join(', ')}`);
  console.log('Reload your agent (e.g. restart Claude Code) to pick up the skill.');
}

// ---------------------------------------------------------------------------
// Subcommands — Sandbox exec (harness-agents PR 1.5 isolation surface)
//
// Runs ONE shell command in a fresh E2B sandbox (create → runCommand → destroy)
// so an operator can exercise the SandboxDriver + egress gate in isolation. The
// E2B key never leaves the backend — the CLI only names the hub + Sandbox
// connection. Platform-admin gated; the strictest rate limit on the platform.
// ---------------------------------------------------------------------------

/**
 * `wayai admin sandbox exec [--cmd "<c>" | <c>] --hub <id> --connection <id>
 *   [--egress open|allowlist|isolated] [--allow <host> ...] [--timeout <ms>] [--json]`
 *
 * The command is a single positional arg (quote it) OR `--cmd`. `--allow` is
 * repeatable. Egress omitted ⇒ the backend fails closed to deny-all.
 */
async function runSandboxExec(positional: string[], flagArgs: string[]): Promise<void> {
  const USAGE =
    'wayai admin sandbox exec [--cmd "<command>" | <command>] --hub <hub_id> --connection <connection_id> ' +
    '[--egress open|allowlist|isolated] [--allow <host> ...] [--timeout <ms>] [--json]';

  let hubId: string | undefined;
  let connectionId: string | undefined;
  let egress: SandboxEgressPolicy | undefined;
  let cmdFlag: string | undefined;
  let timeoutMs: number | undefined;
  let jsonOutput = false;
  const allow: string[] = [];

  for (let i = 0; i < flagArgs.length; i++) {
    const arg = flagArgs[i];
    switch (arg) {
      case '--json':
        jsonOutput = true;
        break;
      case '--hub':
        hubId = requireFlagValue(flagArgs, ++i, '--hub');
        break;
      case '--connection':
        connectionId = requireFlagValue(flagArgs, ++i, '--connection');
        break;
      case '--cmd':
        cmdFlag = requireFlagValue(flagArgs, ++i, '--cmd');
        break;
      case '--egress': {
        const v = requireFlagValue(flagArgs, ++i, '--egress');
        const parsed = sandboxEgressPolicy.safeParse(v);
        if (!parsed.success) {
          console.error(`--egress must be one of: ${sandboxEgressPolicy.options.join(', ')}`);
          process.exit(1);
        }
        egress = parsed.data;
        break;
      }
      case '--allow':
        allow.push(requireFlagValue(flagArgs, ++i, '--allow'));
        break;
      case '--timeout': {
        const v = requireFlagValue(flagArgs, ++i, '--timeout');
        const n = Number.parseInt(v, 10);
        if (!Number.isFinite(n) || n < 1) {
          console.error('--timeout must be a positive integer (milliseconds)');
          process.exit(1);
        }
        timeoutMs = n;
        break;
      }
      default:
        console.error(`Unknown flag: ${arg}`);
        process.exit(1);
    }
  }

  // The command is the sole positional OR `--cmd` (mutually exclusive so an
  // operator doesn't accidentally pass two commands and wonder which ran).
  const positionalCmd = positional[0];
  if (positional.length > 1) {
    console.error('Provide the command as a single quoted argument (or use --cmd).');
    console.error(USAGE);
    process.exit(1);
  }
  if (positionalCmd && cmdFlag) {
    console.error('Provide the command EITHER positionally OR via --cmd, not both.');
    process.exit(1);
  }
  const cmd = cmdFlag ?? positionalCmd;

  if (!cmd || !hubId || !connectionId) {
    console.error(USAGE);
    process.exit(1);
  }

  const body: AdminSandboxExecBody = {
    hub_id: hubId,
    connection_id: connectionId,
    cmd,
    ...(egress ? { egress_policy: egress } : {}),
    ...(allow.length > 0 ? { egress_allowlist: allow } : {}),
    ...(timeoutMs !== undefined ? { timeout_ms: timeoutMs } : {}),
  };

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.adminSandboxExec(body);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const data = response.data;

  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  // Human output: exit code, sandbox id, then stdout/stderr verbatim.
  console.log(`exit_code:  ${data.exit_code}`);
  console.log(`sandbox_id: ${data.sandbox_id}`);
  if (!data.destroyed) {
    console.log('warning:    sandbox destroy failed on the backend (leak logged; cron will reap it)');
  }
  if (data.stdout) {
    console.log('--- stdout ---');
    console.log(data.stdout.replace(/\n$/, ''));
  }
  if (data.stderr) {
    console.log('--- stderr ---');
    console.log(data.stderr.replace(/\n$/, ''));
  }

  // Mirror the command's own exit status so scripting works (non-zero = the
  // sandboxed command failed, distinct from a CLI/transport error above).
  if (data.exit_code !== 0) {
    process.exit(data.exit_code);
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Harness incident response (harness-agents PR 2.9b)
//
// The operator break-glass for runaway/compromised harness agents. `disable`
// flips the platform `harness_enabled` flag OFF so the per-turn kill switch
// aborts each harness turn on its next invocation; `mass-destroy` reaps every
// harness sandbox / token / slot that is live RIGHT NOW. `enable` re-arms.
// Platform-admin gated; `mass-destroy` is DESTRUCTIVE (confirm required).
// ---------------------------------------------------------------------------

/**
 * `wayai admin harness mass-destroy --all | --org <org_id> | --hub <hub_id>
 *   [--dry-run] [--yes] [--json]`
 *
 * Reaps every live harness session in scope. DESTRUCTIVE — requires an
 * interactive y/N confirm unless `--yes`, `--json`, or `--dry-run` is set.
 */
async function runHarnessMassDestroy(flagArgs: string[]): Promise<void> {
  const USAGE =
    'wayai admin harness mass-destroy --all | --org <org_id> | --hub <hub_id> [--dry-run] [--yes] [--json]';

  let all = false;
  let orgId: string | undefined;
  let hubId: string | undefined;
  let dryRun = false;
  let yes = false;
  let jsonOutput = false;

  for (let i = 0; i < flagArgs.length; i++) {
    const arg = flagArgs[i];
    switch (arg) {
      case '--all': all = true; break;
      case '--dry-run': dryRun = true; break;
      case '--yes': yes = true; break;
      case '--json': jsonOutput = true; break;
      case '--org': orgId = requireFlagValue(flagArgs, ++i, '--org'); break;
      case '--hub': hubId = requireFlagValue(flagArgs, ++i, '--hub'); break;
      default:
        console.error(`Unknown flag: ${arg}`);
        process.exit(1);
    }
  }

  // Exactly one scope selector. Resolve it to the contract's { scope, id } shape
  // and let the backend superRefine be the authoritative validator.
  const selectorCount = (all ? 1 : 0) + (orgId ? 1 : 0) + (hubId ? 1 : 0);
  if (selectorCount !== 1) {
    console.error('Provide exactly ONE of --all, --org <org_id>, or --hub <hub_id>.');
    console.error(USAGE);
    process.exit(1);
  }

  let body: AdminHarnessMassDestroyInput;
  let targetLabel: string;
  if (all) {
    body = { scope: 'all', dry_run: dryRun };
    targetLabel = 'ALL hubs (platform-wide)';
  } else if (orgId) {
    body = { scope: 'org', org_id: orgId, dry_run: dryRun };
    targetLabel = `org ${orgId}`;
  } else {
    body = { scope: 'hub', hub_id: hubId!, dry_run: dryRun };
    targetLabel = `hub ${hubId}`;
  }

  // DESTRUCTIVE guard: a real reap (not a dry-run) needs explicit confirmation
  // unless the caller opted out via --yes or is scripting via --json.
  if (!dryRun && !yes && !jsonOutput) {
    console.log(`About to REAP every live harness sandbox / token / slot in: ${targetLabel}.`);
    console.log('This destroys running sandboxes and revokes their tokens. Run with --dry-run first to see the blast radius.');
    const ok = await confirm('Proceed?');
    if (!ok) {
      console.log('Aborted.');
      return;
    }
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let response;
  try {
    response = await client.adminHarnessMassDestroy(body);
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const data = response.data;

  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const mode = data.dry_run ? 'DRY RUN — no reap performed' : 'reaped';
  console.log(
    `harness mass-destroy (${data.scope}) — ${mode}: ` +
    `hubs_scanned=${data.hubs_scanned} sessions_found=${data.sessions_found} ` +
    `sessions_reaped=${data.sessions_reaped} sandboxes_destroyed=${data.sandboxes_destroyed}`,
  );
  // Partial-failure signal: some hubs/sessions threw and were skipped. Surface it
  // loudly so the operator re-runs to cover them (the reap is idempotent).
  if (data.hubs_errored > 0 || data.sessions_errored > 0) {
    console.log(
      `WARNING: ${data.hubs_errored} hub(s) and ${data.sessions_errored} session(s) errored and were SKIPPED — re-run to cover them.`,
    );
  }
  if (data.details.length === 0) {
    console.log('(no live harness sessions in scope)');
    return;
  }
  console.log('');
  console.log('hub_id | conversation_id | had_sandbox | sandbox_destroyed | reaped');
  console.log('-------+-----------------+-------------+-------------------+-------');
  for (const d of data.details) {
    console.log(
      `${d.hub_id} | ${d.conversation_id} | ${d.had_sandbox} | ${d.sandbox_destroyed} | ${d.reaped}`,
    );
  }
}

/**
 * Resolve the target rollout layer from `--org <id>` XOR `--hub <id>`. Neither ⇒
 * the platform layer (the pre-2.9a-2 behavior). Exactly one of org/hub is allowed.
 */
type HarnessLayer =
  | { kind: 'platform' }
  | { kind: 'org'; orgId: string }
  | { kind: 'hub'; hubId: string };

function resolveHarnessLayer(flagArgs: string[], usage: string): HarnessLayer {
  let orgId: string | undefined;
  let hubId: string | undefined;
  for (let i = 0; i < flagArgs.length; i++) {
    const arg = flagArgs[i];
    switch (arg) {
      case '--org': orgId = requireFlagValue(flagArgs, ++i, '--org'); break;
      case '--hub': hubId = requireFlagValue(flagArgs, ++i, '--hub'); break;
      default:
        console.error(`Unknown flag: ${arg}`);
        console.error(usage);
        process.exit(1);
    }
  }
  if (orgId && hubId) {
    console.error('Provide at most ONE of --org <org_id> or --hub <hub_id> (or neither for the platform layer).');
    console.error(usage);
    process.exit(1);
  }
  if (orgId) return { kind: 'org', orgId };
  if (hubId) return { kind: 'hub', hubId };
  return { kind: 'platform' };
}

/**
 * Flip a harness rollout gate (enable=true → 1, false → 0). Targets the platform
 * layer by default, or the ORG/HUB layer via `--org`/`--hub` (harness-agents PR
 * 2.9a-2 staged rollout — a harness agent is creatable only when ALL THREE layers
 * are on).
 */
async function runHarnessToggle(enable: boolean, flagArgs: string[]): Promise<void> {
  const USAGE = 'wayai admin harness enable|disable [--org <org_id> | --hub <hub_id>]';
  const layer = resolveHarnessLayer(flagArgs, USAGE);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let value: number | undefined;
  let scopeLabel: string;
  try {
    if (layer.kind === 'org') {
      const res = await client.adminSetOrgHarness(layer.orgId, enable ? 1 : 0);
      value = res.data.harness_enabled;
      scopeLabel = `org ${layer.orgId}`;
    } else if (layer.kind === 'hub') {
      const res = await client.adminSetHubHarness(layer.hubId, enable ? 1 : 0);
      value = res.data.harness_enabled;
      scopeLabel = `hub ${layer.hubId}`;
    } else {
      const res = await client.adminUpdateConfig({ harness_enabled: enable ? 1 : 0 });
      value = (res.data as { harness_enabled?: number }).harness_enabled;
      scopeLabel = 'platform';
    }
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  const state = enable ? 'ENABLED' : 'DISABLED';
  console.log(`Harness ${state} for ${scopeLabel} (harness_enabled=${value ?? (enable ? 1 : 0)}).`);
  // The per-turn kill switch is PLATFORM-only, so only mention it for the platform layer.
  if (layer.kind === 'platform') {
    if (enable) {
      console.log('New harness turns may run again (subject to each hub\'s + org\'s own flag).');
    } else {
      console.log('The per-turn kill switch now aborts every harness turn on its next invocation.');
      console.log('Next: run `wayai admin harness mass-destroy --all --dry-run` to see what is still live, then reap it.');
    }
  } else {
    console.log('Reminder: a harness agent is creatable only when the platform, org, AND hub flags are ALL on.');
  }
}

/**
 * Print the current `harness_enabled` flag for the chosen layer (platform by
 * default, or the org/hub layer via `--org`/`--hub`).
 */
async function runHarnessStatus(flagArgs: string[]): Promise<void> {
  const USAGE = 'wayai admin harness status [--org <org_id> | --hub <hub_id>]';
  const layer = resolveHarnessLayer(flagArgs, USAGE);

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let value = 0;
  let scopeLabel: string;
  try {
    if (layer.kind === 'org') {
      const res = await client.adminGetOrgHarness(layer.orgId);
      value = res.data.harness_enabled;
      scopeLabel = `org ${layer.orgId}`;
    } else if (layer.kind === 'hub') {
      const res = await client.adminGetHubHarness(layer.hubId);
      value = res.data.harness_enabled;
      scopeLabel = `hub ${layer.hubId}`;
    } else {
      const res = await client.adminGetConfig();
      value = (res.data as { harness_enabled?: number }).harness_enabled ?? 0;
      scopeLabel = 'platform';
    }
  } catch (err) {
    exitOnApiError(err);
    throw err; // unreachable
  }

  console.log(`harness_enabled (${scopeLabel}): ${value} (${value ? 'ENABLED' : 'DISABLED'})`);
}

/** Read the value that follows a value-flag; exit with a clear message if absent. */
function requireFlagValue(flagArgs: string[], index: number, flag: string): string {
  const v = flagArgs[index];
  // Reject an empty string too, not just undefined/`--`-prefixed. An empty flag value
  // is a user error everywhere; for `wayai admin harness enable --org ""` specifically
  // it would otherwise leave orgId='' (falsy), which resolveHarnessLayer treats as
  // "no --org given" and silently retargets the PLATFORM layer — enabling harness
  // platform-wide when the operator meant a single org. Fail loudly instead.
  if (v === undefined || v === '' || v.startsWith('--')) {
    console.error(`${flag} requires a value`);
    process.exit(1);
  }
  return v;
}

function exitOnApiError(err: unknown): void {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      console.error('Authentication failed. Run `wayai login` again.');
      process.exit(1);
    }
    if (err.status === 403) {
      console.error('Access denied. `wayai admin` requires platform-admin access.');
      console.error('Run `wayai whoami` to check your identity and platform-admin status.');
      process.exit(1);
    }
    if (err.status === 404) {
      console.error('Not found.');
      process.exit(1);
    }
    if (err.status === 429) {
      console.error('Rate limited. `wayai admin` is capped at 60 reads/hour per user.');
      process.exit(1);
    }
    if (err.status === 503) {
      // A retryable cold-start (CLICKHOUSE_COLD) that outlived the ApiClient's
      // bounded retry is transient, NOT the kill switch — mislabeling it sends
      // an operator down the wrong path (gh #2883).
      if (err.isRetryable) {
        console.error('ClickHouse is warming up (cold start); the read is temporarily unavailable. Retry in a few seconds.');
      } else {
        console.error('Admin debug reads are currently disabled (platform kill switch).');
      }
      process.exit(1);
    }
    if (err.status === 400) {
      console.error(`Bad request: ${err.body || err.message}`);
      process.exit(1);
    }
    console.error(err.message);
    process.exit(1);
  }
  console.error(err instanceof Error ? err.message : String(err));
  process.exit(1);
}

/**
 * Render the standard debug rows result: --json passes through the raw payload;
 * default mode prints the table + footer with row count, truncation marker, and
 * the redacted-columns summary.
 */
function printDebugRowsResult(
  data: {
    rows: Array<Record<string, unknown>>;
    redacted_columns: string[];
    truncated: boolean;
    limit: number;
  },
  jsonOutput: boolean,
): void {
  if (jsonOutput) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  if (data.rows.length === 0) {
    console.log('(no rows)');
    if (data.redacted_columns.length > 0) {
      console.log(`\nredacted columns: ${data.redacted_columns.join(', ')}`);
    }
    return;
  }

  printRowsTable(data.rows);
  console.log('');
  console.log(`rows: ${data.rows.length}${data.truncated ? ` (truncated to ${data.limit})` : ''}`);
  if (data.redacted_columns.length > 0) {
    console.log(`redacted columns: ${data.redacted_columns.join(', ')}`);
  }
}

/**
 * Pretty-print rows as a columnar text table. Columns are the union of keys
 * across all rows, ordered by first appearance. Values are coerced to strings
 * and truncated at a per-column width so a single row with a huge blob doesn't
 * blow up the whole table.
 */
function printRowsTable(rows: Array<Record<string, unknown>>): void {
  const COL_MAX = 80;
  const cols: string[] = [];
  for (const row of rows) for (const k of Object.keys(row)) if (!cols.includes(k)) cols.push(k);

  const widths: Record<string, number> = {};
  for (const c of cols) widths[c] = Math.min(COL_MAX, Math.max(c.length, ...rows.map((r) => formatValue(r[c]).length)));

  const header = cols.map((c) => c.padEnd(widths[c])).join(' | ');
  const divider = cols.map((c) => '-'.repeat(widths[c])).join('-+-');
  console.log(header);
  console.log(divider);
  for (const row of rows) {
    console.log(cols.map((c) => truncate(formatValue(row[c]), widths[c]).padEnd(widths[c])).join(' | '));
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, Math.max(1, n - 1)) + '…' : s;
}

// ---------------------------------------------------------------------------
// Subcommands — Report (platform_report triage queue)
//
// Consumed by Claude Code Desktop's 15-min triage routine and by humans
// running ad-hoc triage. Mutations (group/escalate/addressed/dismiss) drive
// the state machine in PlatformDO, which is the sole owner of email side
// effects (escalation, resolution, dismissal — `cli` (human) rows only).
// ---------------------------------------------------------------------------

function parseReportFlags(flagArgs: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < flagArgs.length; i++) {
    const flag = flagArgs[i];
    if (!flag.startsWith('--')) continue;
    const key = flag.slice(2);
    const next = flagArgs[i + 1];
    if (next === undefined || next.startsWith('--')) {
      out[key] = 'true';
    } else {
      out[key] = next;
      i++;
    }
  }
  return out;
}

async function runReportList(flagArgs: string[]): Promise<void> {
  const flags = parseReportFlags(flagArgs);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  try {
    const result = await client.listAdminReports({
      status: flags.status,
      source: flags.source,
      limit: flags.limit ? Number(flags.limit) : undefined,
      offset: flags.offset ? Number(flags.offset) : undefined,
      since: flags.since,
    });

    if (flags.json === 'true') {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    console.log(`Reports: ${result.data.length}/${result.total}`);
    for (const r of result.data) {
      const age = Math.floor((Date.now() - r.created_at) / 1000 / 60);
      const meta = [
        r.source,
        r.status,
        // Surface a broken Sentry reference (set at escalation) — these never auto-resolve.
        r.sentry_ref_status && r.sentry_ref_status !== 'valid' ? `sentry_ref:${r.sentry_ref_status}` : null,
        r.gh_issue_url ? `gh:${r.gh_issue_url}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      console.log(`  ${r.report_id}  [${meta}]  ${age}m ago`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportShow(positional: string[]): Promise<void> {
  const reportId = positional[0];
  if (!reportId) {
    console.error('wayai admin report show <report_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.getAdminReport(reportId);
    console.log(JSON.stringify(result.data, null, 2));
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportGroup(positional: string[]): Promise<void> {
  const [canonicalId, ...memberIds] = positional;
  if (!canonicalId || memberIds.length === 0) {
    console.error('wayai admin report group <canonical_id> <member_id> [<member_id> ...]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.groupAdminReports(canonicalId, memberIds);
    console.log(`Grouped ${result.grouped} member(s) under ${result.canonical_id}`);
    for (const s of result.skipped) {
      const reason =
        s.reason === 'different_gh_issue'
          ? `linked to a different GitHub issue (${s.detail})`
          : s.reason === 'grouped_elsewhere'
            ? `already grouped under ${s.detail}`
            : s.reason === 'terminal'
              ? `already ${s.detail} (terminal)`
              : 'not found';
      console.log(`  skipped ${s.report_id} — ${reason}`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportUngroup(positional: string[]): Promise<void> {
  const memberId = positional[0];
  if (!memberId) {
    console.error('wayai admin report ungroup <member_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.ungroupAdminReport(memberId);
    console.log(`Detached ${result.report.report_id} → ${result.report.status}`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportEscalate(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  const ghUrl = flags.gh;
  if (!reportId || !ghUrl) {
    console.error('wayai admin report escalate <report_id> --gh <github_issue_url> [--verdict <text>]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.transitionAdminReport(reportId, {
      target: 'escalated',
      gh_issue_url: ghUrl,
      ai_verdict: flags.verdict,
    });
    console.log(`Report ${result.report.report_id} → ${result.report.status}`);
    // The backend cross-checks a sentry-sourced report's captured reference
    // against the live Sentry issue at escalation. A broken ref still escalates
    // (the bug may be real) but won't auto-resolve on close — flag it loudly.
    const refStatus = result.report.sentry_ref_status;
    if (refStatus === 'not_found') {
      console.log(`  ⚠ Sentry reference not_found — the cited issue does not exist on Sentry. Auto-resolve on close is skipped; verify the reference.`);
    } else if (refStatus === 'mismatch') {
      console.log(`  ⚠ Sentry reference mismatch — issue_id resolves but its short_id ≠ the captured one. Auto-resolve on close is skipped.`);
    } else if (refStatus === 'unverified') {
      console.log(`  ℹ Sentry reference unverified — could not reach Sentry (no token / API error). Auto-resolve on close is skipped.`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportShipped(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  if (!reportId) {
    console.error('wayai admin report shipped <report_id> [--note <what shipped, for the reporter>]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    // `--note` is a value-flag; if omitted, parseReportFlags leaves it undefined and the
    // backend records a default. Don't pass the literal "true" from a value-less `--note`.
    const note = flags.note && flags.note !== 'true' ? flags.note : undefined;
    const result = await client.transitionAdminReport(reportId, { target: 'shipped', note });
    console.log(`Report ${result.report.report_id} → ${result.report.status} (awaiting reporter verification)`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportAddressed(positional: string[]): Promise<void> {
  const reportId = positional[0];
  if (!reportId) {
    console.error('wayai admin report addressed <report_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.transitionAdminReport(reportId, { target: 'addressed' });
    console.log(`Report ${result.report.report_id} → ${result.report.status}`);
    if (result.cascaded_report_ids.length > 0) {
      console.log(`Cascaded ${result.cascaded_report_ids.length} grouped member(s).`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportDismiss(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  const reason = flags.reason;
  if (!reportId || !reason) {
    console.error('wayai admin report dismiss <report_id> --reason <text> [--verdict <text>] [--final]');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    // `--final` makes the dismissal non-contestable (the reporter can't dispute it). Use it to
    // settle a clearly-wrong reporter without waiting for the contest cap.
    const result = await client.transitionAdminReport(reportId, {
      target: 'dismissed',
      dismissal_reason: reason,
      ai_verdict: flags.verdict,
      final: flags.final === 'true',
    });
    console.log(`Report ${result.report.report_id} → ${result.report.status}${flags.final === 'true' ? ' (final — non-contestable)' : ''}`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportHold(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  const flags = parseReportFlags(flagArgs);
  const reason = flags.reason;
  // Reason is mandatory: a hold with no recorded rationale defeats the whole point (an
  // auditable, dateable record of *why* this row is waiting another cycle).
  if (!reportId || !reason || reason === 'true') {
    console.error('wayai admin report hold <report_id> --reason <text>');
    console.error('Defers a pending report one triage cycle (records a note + bumps hold_count). Silent — no email.');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.holdAdminReport(reportId, reason);
    console.log(
      `Report ${result.report.report_id} held (cycle ${result.report.hold_count}) — remains ${result.report.status}.`,
    );
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runReportUpdate(positional: string[], flagArgs: string[]): Promise<void> {
  const reportId = positional[0];
  // Use the dedicated content-flag parser (not parseReportFlags): it errors on a
  // value-less flag and keeps an explicit "" as a clear, so a malformed
  // `--error` can't silently persist the literal "true" into a content field.
  const { patch, error } = parseReportEditArgs(flagArgs);

  if (error) {
    console.error(error);
    process.exit(1);
  }
  if (!reportId || Object.keys(patch).length === 0) {
    console.error('wayai admin report update <report_id> [--title <t>] [--description <d>] [--error <e>] [--steps <s>] [--context <c>]');
    console.error('At least one field to change is required. Pass "" to clear error/steps/context.');
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.updateAdminReport(reportId, patch);
    console.log(`Report ${result.report_id} content updated (status: ${result.status}).`);
    if (result.status === 'escalated') {
      console.log('Note: this edit is queue-local — the linked GitHub issue body is NOT updated.');
    }
    console.log(JSON.stringify(result.raw_payload, null, 2));
  } catch (err) {
    exitOnApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Subcommands — Notices (platform broadcasts, feed 2)
// ---------------------------------------------------------------------------

const VALID_NOTICE_SEVERITIES = ['critical', 'warn', 'info'] as const satisfies readonly NoticeSeverity[];

async function runNoticeCreate(flagArgs: string[]): Promise<void> {
  const flags = parseReportFlags(flagArgs);
  const title = flags.title;
  if (!title || title === 'true') {
    console.error('wayai admin notice create --title <text> [--severity critical|warn|info] [--scope <scope>] [--body <text>] [--link <url>] [--starts <ISO>] [--ends <ISO>]');
    console.error('Authors a platform broadcast (feed 2). Severity defaults to info, scope to global.');
    process.exit(1);
  }
  const severity = (flags.severity ?? 'info') as NoticeSeverity;
  if (!VALID_NOTICE_SEVERITIES.includes(severity)) {
    console.error(`--severity must be one of: ${VALID_NOTICE_SEVERITIES.join(', ')}`);
    process.exit(1);
  }
  // Guard the value-less-flag sentinel (parseReportFlags maps a bare `--scope` to 'true')
  // so it fails locally with a clear message instead of as a backend regex 400.
  if (flags.scope === 'true') {
    console.error('--scope requires a value (e.g. global, provider:gemini, service:analytics)');
    process.exit(1);
  }

  const body: CreateNoticeBody = {
    severity,
    scope: flags.scope ?? 'global',
    title,
    ...(flags.body && flags.body !== 'true' ? { body: flags.body } : {}),
    ...(flags.link && flags.link !== 'true' ? { link: flags.link } : {}),
    ...(flags.starts && flags.starts !== 'true' ? { starts_at: flags.starts } : {}),
    ...(flags.ends && flags.ends !== 'true' ? { ends_at: flags.ends } : {}),
  };

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.createAdminNotice(body);
    const n = result.data;
    console.log(`Created notice ${n.notice_id} — [${n.severity} · ${n.scope}] ${n.title}`);
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runNoticeList(flagArgs: string[]): Promise<void> {
  const flags = parseReportFlags(flagArgs);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.listAdminNotices({
      status: flags.status,
      limit: flags.limit ? Number(flags.limit) : undefined,
      offset: flags.offset ? Number(flags.offset) : undefined,
    });

    if (flags.json === 'true') {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    console.log(`Notices: ${result.data.length}/${result.total}`);
    for (const n of result.data) {
      const meta = [n.severity, n.scope, n.status, n.ends_at ? `ends:${n.ends_at}` : null]
        .filter(Boolean)
        .join(' · ');
      console.log(`  ${n.notice_id}  [${meta}]  ${n.title}`);
    }
  } catch (err) {
    exitOnApiError(err);
  }
}

async function runNoticeResolve(positional: string[]): Promise<void> {
  const noticeId = positional[0];
  if (!noticeId) {
    console.error('wayai admin notice resolve <notice_id>');
    process.exit(1);
  }
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const result = await client.resolveAdminNotice(noticeId);
    console.log(`Notice ${result.data.notice_id} resolved.`);
  } catch (err) {
    exitOnApiError(err);
  }
}

function printHelp(): void {
  console.error(`
wayai admin — platform-admin diagnostic commands

Usage:
  wayai admin do tables <type> <id>
  wayai admin do read <type> <id> <table> [--limit N] [--id X] [--json]

  wayai admin analytics tables
  wayai admin analytics read <table> [--conversation-id X] [--hub-id Y] [--message-id Z] [--limit N] [--json]

  wayai admin archive <hub_id> <conversation_id> [--message-id X] [--json]

  wayai admin observability <hub_id> <conversation_id> [--message-id X] [--json]

  wayai admin report list [--status STATUS] [--source SOURCE] [--limit N] [--offset N] [--since ISO] [--json]
  wayai admin report show <report_id>
  wayai admin report group <canonical_id> <member_id> [<member_id> ...]
  wayai admin report ungroup <member_id>
  wayai admin report escalate <report_id> --gh <github_issue_url> [--verdict <text>]
  wayai admin report shipped <report_id> [--note <what shipped, for the reporter>]
  wayai admin report addressed <report_id>
  wayai admin report dismiss <report_id> --reason <text> [--verdict <text>] [--final]
  wayai admin report hold <report_id> --reason <text>
  wayai admin report update <report_id> [--title <t>] [--description <d>] [--error <e>] [--steps <s>] [--context <c>]

  wayai admin notice create --title <text> [--severity critical|warn|info] [--scope <scope>] [--body <text>] [--link <url>] [--starts <ISO>] [--ends <ISO>]
  wayai admin notice list [--status active|resolved] [--limit N] [--offset N] [--json]
  wayai admin notice resolve <notice_id>

  wayai admin template push <slug>                 Publish a template from wayai-templates/<slug>/ into the store
  wayai admin template pull <slug> [--out DIR] [--json]   Pull a published template from the store for inspection
  wayai admin template freeze <slug> [--locale <l>] [--from <hub>] [--dry-run]
                                                   Snapshot a live hub into the template's committed slice (id/PII-scrubbed)

  wayai admin skill install [name]                 Install a private operator skill (default: wayai-admin) into each present harness's skills/ dir

  wayai admin sandbox exec [--cmd "<c>" | <c>] --hub <id> --connection <id> [--egress open|allowlist|isolated] [--allow <host> ...] [--timeout <ms>] [--json]
                                                   Run one shell command in a fresh E2B sandbox (create → run → destroy) to test the sandbox driver + egress gate

  wayai admin harness disable [--org <org_id> | --hub <hub_id>]
                                                   Flip harness_enabled OFF. No scope = platform (break-glass: per-turn kill switch aborts each harness turn on its next invocation); --org/--hub target that layer of the three-layer rollout gate
  wayai admin harness enable [--org <org_id> | --hub <hub_id>]
                                                   Flip harness_enabled ON. No scope = platform; --org/--hub stage the org/hub layer (a harness agent is creatable only when platform AND org AND hub are ALL on)
  wayai admin harness status [--org <org_id> | --hub <hub_id>]
                                                   Print the harness_enabled flag for the chosen layer (platform by default)
  wayai admin harness mass-destroy --all | --org <org_id> | --hub <hub_id> [--dry-run] [--yes] [--json]
                                                   Reap every live harness sandbox/token/slot in scope (run --dry-run first to see the blast radius)

Sources:
  do             Live DO SQLite (existing conversation, hub config, etc.).
  analytics      ClickHouse projections (conversation, message, billing_event, schedule_event).
  archive        R2 archive blob — canonical content for ended conversations after DO cleanup.
  observability  R2 LLM observability — per-message OTel GenAI records (prompts, completions,
                 tool calls, tokens, latency). Default lists all records for the conversation;
                 --message-id drills into one. Use this admin variant when the caller has no
                 hub access; otherwise prefer \`wayai conversations <id> observability\`.
  report         platform_report triage queue. \`list/show\` are reads; \`group/ungroup/
                 escalate/addressed/dismiss\` drive the state machine (which owns email
                 fanout for cli (human) rows). \`ungroup\` detaches a grouped member back
                 to pending (inverse of \`group\`). \`hold\` defers a pending row one cycle
                 (records an internal note + bumps hold_count) without transitioning it —
                 silent for every source. \`update\` amends content fields
                 (title/description/error/steps/context) while pending/escalated — the
                 dedup fingerprint stays frozen and no email fires. Agent/Sentry rows
                 never trigger emails.
  notice         platform_notice broadcasts (Status & Notices feed 2) — operator-authored
                 "Gemini degraded" / "Analytics delayed" notices. \`create\` authors one
                 (severity defaults to info, scope to global); \`list\` reads them
                 (--status active|resolved); \`resolve\` ends an active notice.
  sandbox        Run arbitrary code in a fresh E2B sandbox (harness-agents isolation surface).
                 \`exec\` provisions a sandbox from a named Sandbox connection, runs one
                 command, and destroys it. Egress omitted ⇒ deny-all (fail-closed); opt up
                 with --egress + --allow. The E2B key never leaves the backend. Capped at
                 20 execs/hour/user (the strictest tier — this is arbitrary code execution).
  harness        Incident response + staged rollout for harness agents. \`disable\`/\`enable\`
                 flip the harness_enabled flag; \`status\` prints it. With no scope they
                 target the PLATFORM flag (disable = break-glass: the per-turn kill switch
                 then aborts each harness turn on its next invocation). With --org <id> or
                 --hub <id> they target that layer of the three-layer rollout gate (PR
                 2.9a-2): a harness agent is creatable only when platform AND org AND hub
                 are ALL on — flip platform on, then enable per org/hub to stage. The
                 per-turn kill switch stays platform-only. \`mass-destroy\` reaps every
                 harness sandbox/token/slot that is live NOW (scope: --hub < --org < --all,
                 prefer the narrowest). Run \`mass-destroy --dry-run\` first to see the blast
                 radius. See docs/runbooks/harness-incident-response.md.

Types (do): ${VALID_TYPES.join(' | ')}
Tables (analytics): ${VALID_ANALYTICS_TABLES.join(' | ')}

Entity id forms (do):
  <uuid>          Domain entity id (hub_id, org_id, conversation_id, user_id) —
                  hashed via idFromName, same path as every other route.
  hex:<64-hex>    Raw CF DO hex id — resolved via idFromString. Use when you
                  only have the CF-level id (e.g. from orphan-audit logs, CF
                  traces, zombie instances with no registry entry).
  platform        Singleton — literal string, hex: form not supported.

Examples:
  wayai admin do tables hub 018fab...
  wayai admin do read conversation <conv-id> message --limit 5 --json
  wayai admin analytics read message --conversation-id <conv-id>
  wayai admin analytics read conversation --conversation-id <conv-id>
  wayai admin archive <hub-id> <conv-id>
  wayai admin archive <hub-id> <conv-id> --message-id <msg-id> --json
  wayai admin observability <hub-id> <conv-id>
  wayai admin observability <hub-id> <conv-id> --message-id <msg-id> --json
  wayai admin sandbox exec "echo hi" --hub <hub-id> --connection <conn-id>
  wayai admin sandbox exec --cmd "curl -s https://api.github.com" --hub <hub-id> --connection <conn-id> --egress allowlist --allow api.github.com
  wayai admin harness disable
  wayai admin harness mass-destroy --all --dry-run
  wayai admin harness mass-destroy --hub <hub-id> --yes
  wayai admin harness enable

Audit + rate limit:
  Every successful read is logged as admin_debug_read (queryable in Grafana
  Loki — info-level events are NOT sent to Sentry). Capped at 60 reads/hour/user
  across all sources. Platform-admin grant required.
`.trim());
}
